import {
	Currency,
	Form,
	Grid,
	Header,
	Popup,
	Segment,
} from '@mairistem/composants';
import type { Exercice } from '@mairistem/comptabilite';
import { formatMontant, Icon } from '@mairistem/finances-utils';
import { isNewIdentifiant } from '@mairistem/tools';
import { Decimal } from 'decimal.js';
import React from 'react';
import type Financement from '../../../interfaces/common/financement';
import type ProImputation from '../../../interfaces/common/proImputation';
import ProImputationList from './ProImputationList';
import './FinancementItem.css';
import DECISION from '../../../constants/common/decision';

interface FinancementItemProps {
	decision: number;
	exercices: Exercice[];
	exercicesGestionTva: Set<number>;
	exercicesReportFonc: Set<number>;
	exercicesSansEspace: Set<number>;
	financement: Financement;
	identifiantService: number | null;
	/** La gestion par service est disponible et un service est selectionne */
	isGestionParService: boolean;
	onAddProImputation: () => void;
	onChangeFinancement: (data: Partial<Financement>) => void;
	onChangeProImputation: (
		data: Partial<ProImputation>,
		identifiantProImputation: number | string,
	) => void;
	onRemoveFinancement: () => void;
	onRemoveProImputation: (identifiantProImputation: number | string) => void;
}

const FinancementItem = ({
	decision,
	exercices,
	exercicesGestionTva,
	exercicesReportFonc,
	exercicesSansEspace,
	financement,
	identifiantService,
	isGestionParService,
	onAddProImputation,
	onChangeFinancement,
	onChangeProImputation,
	onRemoveFinancement,
	onRemoveProImputation,
}: FinancementItemProps) => {
	const [isAccordionActive, setIsAccordionActive] = React.useState(
		isNewIdentifiant(financement.identifiant),
	);

	// En gestion par service, l'exercice ne peut plus etre modifie une fois des lignes ajoutees (elles dependent de l'espace du service sur l'exercice)
	const isExerciceLocked =
		isGestionParService && financement.proImputations?.length > 0;

	const handleBlurFinancementExercice = ({
		target: { name, value },
	}: React.FocusEvent<HTMLInputElement>) => {
		const numberedValue = Number(value);
		const foundExercice = exercices.find(
			(exercice) => exercice.anneeExercice === numberedValue,
		);

		onChangeFinancement({
			[name]: numberedValue,
			...(foundExercice && { identifiantExercice: foundExercice.identifiant }),
		});
	};

	const handleBlurFinancementMontant = (
		{ target: { name } }: React.FocusEvent<HTMLInputElement>,
		montant: number,
	) => {
		onChangeFinancement({ [name]: montant });
	};

	const handleClickAccordion = () => {
		setIsAccordionActive((old) => !old);
	};

	return (
		<Segment>
			<Form>
				<Grid>
					<Grid.Row>
						<Grid.Column width={1}>
							<Popup
								content="L'exercice ne peut plus être modifié car des lignes budgétaires sont saisies"
								disabled={!isExerciceLocked}
								position="top left"
								trigger={
									// Un champ desactive ne declenche pas d'evenement souris : le div porte le survol
									<div>
										<Form.Input
											defaultValue={financement.exercice}
											disabled={isExerciceLocked}
											input={{
												'data-testid': `exerciceFinancementInput${financement.identifiant}`,
												maxLength: 4,
											}}
											label="Exercice"
											name="exercice"
											onBlur={handleBlurFinancementExercice}
										/>
									</div>
								}
							/>
						</Grid.Column>
						<Grid.Column width={3}>
							<Currency
								data-testid={`montantFinancementCurrency${financement.identifiant}`}
								label="Crédit initial"
								name="montant"
								onBlur={handleBlurFinancementMontant}
								value={financement.montant}
							/>
						</Grid.Column>
						<Grid.Column width={3}>
							<Currency
								data-testid={`revisionFinancementCurrency${financement.identifiant}`}
								label="Rééchelonnement"
								name="revision"
								onBlur={handleBlurFinancementMontant}
								value={financement.revision}
							/>
						</Grid.Column>
						<Grid.Column width={3}>
							<Currency
								data-testid={`rarNFinancementCurrency${financement.identifiant}`}
								label={`RAR ${financement.exercice ? financement.exercice : ''}`}
								name="rarN"
								onBlur={handleBlurFinancementMontant}
								value={financement.rarN}
								disabled={
									!exercicesReportFonc.has(financement.exercice) &&
									decision === DECISION.AE
								}
							/>
						</Grid.Column>
						<Grid.Column width={3}>
							<Currency
								data-testid={`rarN1FinancementCurrency${financement.identifiant}`}
								label={`RAR ${financement.exercice ? financement.exercice - 1 : ''}`}
								name="rarN1"
								onBlur={handleBlurFinancementMontant}
								value={financement.rarN1}
								disabled={
									!exercicesReportFonc.has(financement.exercice) &&
									decision === DECISION.AE
								}
							/>
						</Grid.Column>
						<Grid.Column textAlign="right" verticalAlign="middle" width={2}>
							<Header size="tiny">
								<Header.Content>
									Total
									<br />
									{formatMontant(
										new Decimal(financement.montant)
											.add(financement.revision)
											.add(financement.rarN1)
											.sub(financement.rarN)
											.toNumber(),
									)}
								</Header.Content>
							</Header>
						</Grid.Column>
						<Grid.Column textAlign="center" verticalAlign="middle" width={1}>
							<Icon
								color="red"
								data-testid={`removeFinancementButton${financement.identifiant}`}
								iconSet="Lucide"
								name="Trash2"
								onClick={onRemoveFinancement}
								size={16}
							/>
						</Grid.Column>
					</Grid.Row>
				</Grid>
			</Form>
			<Header
				className="financementItemAccordion"
				content="Imputation budgétaire"
				data-testid={`financementItemAccordion${financement.identifiant}`}
				icon={`chevron ${isAccordionActive ? 'down' : 'up'}`}
				onClick={handleClickAccordion}
				size="tiny"
			/>
			{isAccordionActive && (
				<ProImputationList
					decision={decision}
					exercices={exercices}
					exercicesGestionTva={exercicesGestionTva}
					exercicesReportFonc={exercicesReportFonc}
					exercicesSansEspace={exercicesSansEspace}
					financement={financement}
					identifiantService={identifiantService}
					isGestionParService={isGestionParService}
					onAddProImputation={onAddProImputation}
					onChangeProImputation={onChangeProImputation}
					onRemoveProImputation={onRemoveProImputation}
				/>
			)}
		</Segment>
	);
};

export default FinancementItem;
